import React, { useCallback, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { CloseCircleOutlined, FolderOpenOutlined, EditOutlined, CopyOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)
import { SwipeRevealCard, SwipeAction } from '@pushly/aqe/lib/components/swipe-reveal-card/swipe-reveal-card'
import { StatusBadge, SourceBadge, MetricBadge } from '@pushly/aqe/lib/components/badges'
import { AudienceBadge } from '../../../components/badges/audience-badge'
import { mapNotificationAudienceToBadge } from '../../../components/badges/audience-badge-mappers'
import { NotificationQuickSheet } from '../../notification-quick-sheet/notification-quick-sheet'
import { AiGeneratedBadge } from '../../../components/badges/ai-generated-badge'
import { useAppState } from '../../hooks/use-mobile-service'
import { useNotificationActions } from '../../hooks/use-notification-actions'
import { useNotificationPermissions } from '../../hooks/use-notification-permissions'
import { NotificationCardData } from '../../../hooks/use-notifications'
import { StatusType } from '../../../enums/status-type'
import { NotificationSource } from '@pushly/models/lib/enums/notification-source'
import './notification-card.scss'

interface NotificationCardProps {
    notification: NotificationCardData
    /** Controls swipe actions and tap behavior. Default: 'notification' */
    variant?: 'notification' | 'draft'
    onChange?: () => void
    /** Called when a draft is archived (variant='draft' only) */
    onArchive?: (notificationId: number) => void
    /** When true, plays a peek animation to hint at swipe actions */
    hintSwipe?: boolean
    /** Called when the user successfully swipes to reveal actions */
    onSwipeReveal?: () => void
    /** Hide the status badge (e.g., in insights where all are COMPLETED) */
    hideStatus?: boolean
    /** Replaces the status badge with a MetricBadge. Requires metric + formatted value. */
    metricDisplay?: { metric: 'impressions' | 'clicks' | 'ctr'; value: string }
    /** When true, disables swipe actions (e.g., insights cards are read-only) */
    disableActions?: boolean
}

export const NotificationCard: React.FC<NotificationCardProps> = observer(
    ({
        notification: notif,
        variant = 'notification',
        onChange,
        onArchive,
        hintSwipe,
        onSwipeReveal,
        hideStatus,
        metricDisplay,
        disableActions,
    }) => {
        const appState = useAppState()
        const [sheetOpen, setSheetOpen] = useState(false)

        const domainId = appState.currentDomain?.id
        const timezone = appState.currentDomain?.timezone ?? 'UTC'

        // Read permissions reactively via hook (MobX observer tracks changes)
        const { canCreate, canUpdate } = useNotificationPermissions()

        const isDraft = variant === 'draft'

        const formattedTime = useMemo(() => {
            if (!notif.sendTime) return ''
            return dayjs.utc(notif.sendTime).tz(timezone).format('MMM D, YYYY [at] h:mm A z')
        }, [notif.sendTime, timezone])

        const lastEditedLabel = useMemo(() => {
            if (!isDraft || !notif.lastEditedAt) return null
            // If never edited (created_at === updated_at), show "Created" instead of "Edited"
            const created = notif.createdAt
            const edited = notif.lastEditedAt
            if (created && edited && Math.abs(dayjs(created).diff(dayjs(edited), 'second')) < 2) {
                return `Created ${dayjs.utc(created).tz(timezone).fromNow()}`
            }
            return `Edited ${dayjs.utc(edited).tz(timezone).fromNow()}`
        }, [isDraft, notif.lastEditedAt, notif.createdAt, timezone])

        const ctrDisplay = useMemo(() => {
            if (isDraft) return null
            if (notif.ctrDecimal != null && notif.ctrDecimal > 0) {
                return `${(notif.ctrDecimal * 100).toFixed(2)}%`
            }
            return null
        }, [notif.ctrDecimal, isDraft])

        const clicksDisplay = useMemo(() => {
            if (isDraft) return null
            if (notif.clicks != null && notif.clicks > 0) {
                return notif.clicks.toLocaleString()
            }
            return null
        }, [notif.clicks, isDraft])

        const handleTap = useCallback(() => {
            requestAnimationFrame(() => {
                setSheetOpen(true)
            })
        }, [])

        const closeSheet = useCallback(() => setSheetOpen(false), [])

        const {
            handleEdit,
            handleDuplicate,
            handleCancel,
            handleArchive: handleArchiveAction,
        } = useNotificationActions({
            domainId,
            notificationId: notif.notificationId,
            title: notif.title,
            scheduleId: notif.id,
            onBeforeNavigate: closeSheet,
            onChange,
        })

        const handleArchive = useCallback(() => {
            if (!onArchive) return
            handleArchiveAction(onArchive)
        }, [handleArchiveAction, onArchive])

        const swipeActions = useMemo((): SwipeAction[] => {
            const actions: SwipeAction[] = []

            if (isDraft) {
                // Draft swipe actions — order: Archive (destructive, inner) → Duplicate (middle) → Edit (primary, edge)
                if (canUpdate) {
                    actions.push({
                        key: 'archive',
                        label: 'Archive',
                        icon: <FolderOpenOutlined />,
                        backgroundColor: 'var(--color-red-100)',
                        color: '#ffffff',
                        onAction: handleArchive,
                    })
                }
                if (canCreate) {
                    actions.push({
                        key: 'duplicate',
                        label: 'Duplicate',
                        icon: <CopyOutlined />,
                        backgroundColor: 'var(--color-secondary-100)',
                        color: '#ffffff',
                        onAction: handleDuplicate,
                    })
                }
                if (canUpdate) {
                    actions.push({
                        key: 'edit',
                        label: 'Edit',
                        icon: <EditOutlined />,
                        backgroundColor: 'var(--color-tertiary-100)',
                        color: '#ffffff',
                        onAction: handleEdit,
                    })
                }
            } else {
                // Notification swipe actions — order: Cancel (destructive, inner) → Duplicate (middle) → Edit (primary, edge)
                const isScheduled = notif.status === StatusType.SCHEDULED.name

                if (isScheduled && canUpdate) {
                    actions.push({
                        key: 'cancel',
                        label: 'Cancel',
                        icon: <CloseCircleOutlined />,
                        backgroundColor: 'var(--color-red-100)',
                        color: '#ffffff',
                        onAction: handleCancel,
                    })
                }

                if (canCreate) {
                    actions.push({
                        key: 'duplicate',
                        label: 'Duplicate',
                        icon: <CopyOutlined />,
                        backgroundColor: 'var(--color-secondary-100)',
                        color: '#ffffff',
                        onAction: handleDuplicate,
                    })
                }

                if (isScheduled && canUpdate) {
                    actions.push({
                        key: 'edit',
                        label: 'Edit',
                        icon: <EditOutlined />,
                        backgroundColor: 'var(--color-tertiary-100)',
                        color: '#ffffff',
                        onAction: handleEdit,
                    })
                }
            }

            return actions
        }, [isDraft, notif.status, canCreate, canUpdate, handleCancel, handleEdit, handleDuplicate, handleArchive])

        // Card stat line: CTR for delivered notifications (hidden for drafts)
        const cardStat = useMemo(() => {
            if (ctrDisplay) return `${ctrDisplay} CTR`
            if (clicksDisplay) return `${clicksDisplay} Clicks`
            return null
        }, [ctrDisplay, clicksDisplay])

        return (
            <>
                <SwipeRevealCard
                    actions={disableActions ? [] : swipeActions}
                    className="notification-card"
                    onTap={handleTap}
                    hintOnMount={hintSwipe}
                    onSwipeReveal={onSwipeReveal}
                >
                    <div
                        className="notification-card__content"
                        role="button"
                        tabIndex={0}
                        aria-label={`${notif.title}, ${notif.status}`}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleTap()
                            }
                        }}
                    >
                        <div className="notification-card__top">
                            {notif.imageUrl && (
                                <img
                                    className="notification-card__thumb"
                                    src={notif.imageUrl}
                                    alt=""
                                    aria-hidden="true"
                                    loading="lazy"
                                />
                            )}
                            <div className="notification-card__top-text">
                                <div className="notification-card__header">
                                    <span className="notification-card__title">{notif.title}</span>
                                    {metricDisplay ? (
                                        <MetricBadge metric={metricDisplay.metric} value={metricDisplay.value} />
                                    ) : !hideStatus ? (
                                        <StatusBadge type="notification" status={notif.status} expanded />
                                    ) : null}
                                </div>
                                <div className="notification-card__body-row">
                                    <span className="notification-card__body">{notif.body ?? ''}</span>
                                    {notif.aiSource === 'pushly_ai' && (
                                        <AiGeneratedBadge source={notif.aiSource} showMetadata={false} />
                                    )}
                                    {notif.source && notif.source !== NotificationSource.MANUAL && (
                                        <SourceBadge type="notification" source={notif.source} expanded />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="notification-card__meta">
                            {notif.audience && (
                                <AudienceBadge
                                    audience={mapNotificationAudienceToBadge(notif.audience)}
                                    hideTooltip
                                    disableLinks
                                />
                            )}
                            {cardStat && !metricDisplay && (
                                <>
                                    <span className="notification-card__meta-sep">·</span>
                                    <span className="notification-card__stat">{cardStat}</span>
                                </>
                            )}
                        </div>
                        <div className="notification-card__time">
                            <span>{formattedTime}</span>
                            {lastEditedLabel && (
                                <span className="notification-card__last-edited">{lastEditedLabel}</span>
                            )}
                        </div>
                    </div>
                </SwipeRevealCard>

                <NotificationQuickSheet
                    notificationId={sheetOpen ? notif.notificationId : null}
                    open={sheetOpen}
                    onClose={() => setSheetOpen(false)}
                    onChange={onChange}
                    onArchive={isDraft ? onArchive : undefined}
                    variant={isDraft ? 'draft' : 'notification'}
                />
            </>
        )
    },
)

NotificationCard.displayName = 'NotificationCard'
